import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react';
import styles from './CitySelector.module.css';
import { Modal } from '../../../../components/UI/Modal/Modal';
import { Button } from '../../../../components/UI/Button/Button';
import { useApi } from '../../../../hooks/useApi';
import { CityModel, CountryModel } from '../../../../models/locations';
import {
  CreateCityRequest,
  UpdateCityRequest,
} from '../../../../api/reviews/dto/LocationsRequest.dto';

interface CitySelectorProps {
  value?: string;
  onChange: (cityId: string) => void;
  placeholder?: string;
  disabled?: boolean;
}

export const CitySelector: React.FC<CitySelectorProps> = observer(
  ({ value, onChange, placeholder = 'Выберите город', disabled = false }) => {
    const api = useApi();
    const [cities, setCities] = useState<CityModel[]>([]);
    const [countries, setCountries] = useState<CountryModel[]>([]);
    const [loading, setLoading] = useState(false);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [createLoading, setCreateLoading] = useState(false);
    const [editingCity, setEditingCity] = useState<CityModel | null>(null);
    const [editLoading, setEditLoading] = useState(false);

    const selectedCity = cities.find((city) => city.id === value);

    useEffect(() => {
      loadCities();
      loadCountries();
    }, []);

    // Вспомогательные функции для работы с переводами
    const getTranslation = (
      translations: (typeof cities)[0]['translations'],
      locale: 'ru' | 'en',
    ) => {
      return translations.find((t) => t.locale === locale);
    };

    const updateTranslation = (
      city: CityModel,
      locale: 'ru' | 'en',
      name: string,
    ): CityModel => {
      return {
        ...city,
        translations: city.translations.map((t) =>
          t.locale === locale ? { ...t, name } : t,
        ),
      };
    };

    const loadCities = async () => {
      setLoading(true);
      try {
        const response = await api.reviews.getCities();
        setCities(response.body?.cities || []);
      } catch (error) {
        // Ошибка обработается в родительском компоненте
        setCities([]);
      } finally {
        setLoading(false);
      }
    };

    const loadCountries = async () => {
      try {
        const response = await api.reviews.getCountries();
        setCountries(response.body?.countries || []);
      } catch (error) {
        // Ошибка обработается в родительском компоненте
        setCountries([]);
      }
    };

    const handleCreateCity = async (
      event: React.FormEvent<HTMLFormElement>,
    ) => {
      event.preventDefault();
      setCreateLoading(true);

      const formData = new FormData(event.currentTarget);
      const cityData: CreateCityRequest = {
        country_id: formData.get('country_id') as string,
        translations: [
          {
            locale: 'ru',
            name: formData.get('name_ru') as string,
          },
          {
            locale: 'en',
            name: formData.get('name_en') as string,
          },
        ],
      };

      try {
        const response = await api.reviews.createCity(cityData);
        if (response.body) {
          setCities((prev) => [...prev, response.body as CityModel]);
          onChange(response.body.id);
          setIsCreateModalOpen(false);
          event.currentTarget.reset();
        }
      } catch (error) {
        // Ошибка обработается в родительском компоненте
      } finally {
        setCreateLoading(false);
      }
    };

    const handleEditCity = async () => {
      if (!editingCity) {
        return;
      }

      const ruTranslation = getTranslation(editingCity.translations, 'ru');
      const enTranslation = getTranslation(editingCity.translations, 'en');

      if (!ruTranslation?.name || !enTranslation?.name) {
        return;
      }

      setEditLoading(true);

      try {
        const updateData: UpdateCityRequest = {
          country_id: editingCity.country_id,
          translations: [
            {
              locale: 'ru',
              name: ruTranslation.name,
            },
            {
              locale: 'en',
              name: enTranslation.name,
            },
          ],
        };
        const response = await api.reviews.updateCity(
          editingCity.id,
          updateData,
        );

        if (response.body) {
          setCities((prev) =>
            prev.map((city) =>
              city.id === editingCity.id ? (response.body as CityModel) : city,
            ),
          );
          setEditingCity(null);
        }
      } catch (error) {
        // Ошибка обработается в родительском компоненте
      } finally {
        setEditLoading(false);
      }
    };

    const handleDeleteCity = async (cityId: string) => {
      if (!confirm('Вы уверены, что хотите удалить этот город?')) {
        return;
      }

      try {
        await api.reviews.deleteCity(cityId);
        setCities((prev) => prev.filter((city) => city.id !== cityId));
        if (value === cityId) {
          onChange('');
        }
      } catch (error) {
        // Ошибка обработается в родительском компоненте
      }
    };

    const getCountryName = (countryId: string) => {
      const country = countries.find((c) => c.id === countryId);
      if (!country) {
        return 'Неизвестная страна';
      }

      const ruTranslation = getTranslation(country.translations, 'ru');
      const enTranslation = getTranslation(country.translations, 'en');

      return `${ruTranslation?.name || country.iso2} (${
        enTranslation?.name || country.iso2
      })`;
    };

    return (
      <div className={styles.citySelector}>
        <div className={styles.selectRow}>
          <select
            className={styles.select}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled || loading}
          >
            <option value="">{loading ? 'Загрузка...' : placeholder}</option>
            {cities.map((city) => (
              <option key={city.id} value={city.id}>
                {getTranslation(city.translations, 'ru')?.name ||
                  'Неизвестный город'}{' '}
                (
                {getTranslation(city.translations, 'en')?.name ||
                  'Unknown city'}
                ) - {getCountryName(city.country_id)}
              </option>
            ))}
          </select>
          <Button
            type="button"
            className={styles.addButton}
            onClick={() => setIsCreateModalOpen(true)}
            disabled={disabled}
            appearance="auth"
          >
            +
          </Button>
        </div>

        {selectedCity && (
          <div className={styles.selectedInfo}>
            <div className={styles.cityInfo}>
              <strong>
                {getTranslation(selectedCity.translations, 'ru')?.name ||
                  'Неизвестный город'}{' '}
                (
                {getTranslation(selectedCity.translations, 'en')?.name ||
                  'Unknown city'}
                )
              </strong>
              <br />
              Страна: {getCountryName(selectedCity.country_id)}
            </div>
            <div className={styles.cityActions}>
              <Button
                type="button"
                className={styles.actionButton}
                onClick={() => setEditingCity(selectedCity)}
                appearance="auth"
              >
                Изменить
              </Button>
              <Button
                type="button"
                className={styles.deleteButton}
                onClick={() => handleDeleteCity(selectedCity.id)}
                appearance="auth"
              >
                Удалить
              </Button>
            </div>
          </div>
        )}

        {/* Модальное окно создания города */}
        <Modal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          title="Добавить город"
        >
          <form className={styles.createForm} onSubmit={handleCreateCity}>
            <div className={styles.formGroup}>
              <label className={styles.label}>Название города (русский)*</label>
              <input
                type="text"
                name="name_ru"
                className={styles.input}
                placeholder="Введите название на русском"
                required
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>
                Название города (английский)*
              </label>
              <input
                type="text"
                name="name_en"
                className={styles.input}
                placeholder="Введите название на английском"
                required
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Страна*</label>
              <select name="country_id" className={styles.input} required>
                <option value="">Выберите страну</option>
                {countries.map((country) => (
                  <option key={country.id} value={country.id}>
                    {getTranslation(country.translations, 'ru')?.name ||
                      country.iso2}{' '}
                    (
                    {getTranslation(country.translations, 'en')?.name ||
                      country.iso2}
                    )
                  </option>
                ))}
              </select>
            </div>

            <div className={styles.modalActions}>
              <Button
                type="button"
                appearance="link"
                onClick={() => setIsCreateModalOpen(false)}
              >
                Отмена
              </Button>
              <Button type="submit" appearance="auth" disabled={createLoading}>
                {createLoading ? 'Создание...' : 'Создать город'}
              </Button>
            </div>
          </form>
        </Modal>

        {/* Модальное окно редактирования города */}
        <Modal
          isOpen={!!editingCity}
          onClose={() => setEditingCity(null)}
          title="Редактировать город"
        >
          {editingCity && (
            <div className={styles.createForm}>
              <div className={styles.formGroup}>
                <label className={styles.label}>
                  Название города (русский)*
                </label>
                <input
                  type="text"
                  className={styles.input}
                  value={
                    getTranslation(editingCity.translations, 'ru')?.name || ''
                  }
                  onChange={(e) =>
                    setEditingCity((prev) =>
                      prev
                        ? updateTranslation(prev, 'ru', e.target.value)
                        : null,
                    )
                  }
                  placeholder="Введите название на русском"
                  required
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>
                  Название города (английский)*
                </label>
                <input
                  type="text"
                  className={styles.input}
                  value={
                    getTranslation(editingCity.translations, 'en')?.name || ''
                  }
                  onChange={(e) =>
                    setEditingCity((prev) =>
                      prev
                        ? updateTranslation(prev, 'en', e.target.value)
                        : null,
                    )
                  }
                  placeholder="Введите название на английском"
                  required
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Страна*</label>
                <select
                  className={styles.input}
                  value={editingCity.country_id}
                  onChange={(e) =>
                    setEditingCity((prev) =>
                      prev ? { ...prev, country_id: e.target.value } : null,
                    )
                  }
                  required
                >
                  <option value="">Выберите страну</option>
                  {countries.map((country) => (
                    <option key={country.id} value={country.id}>
                      {getTranslation(country.translations, 'ru')?.name ||
                        country.iso2}{' '}
                      (
                      {getTranslation(country.translations, 'en')?.name ||
                        country.iso2}
                      )
                    </option>
                  ))}
                </select>
              </div>

              <div className={styles.modalActions}>
                <Button
                  type="button"
                  appearance="link"
                  onClick={() => setEditingCity(null)}
                >
                  Отмена
                </Button>
                <Button
                  type="button"
                  appearance="auth"
                  onClick={handleEditCity}
                  disabled={
                    !getTranslation(editingCity.translations, 'ru')?.name ||
                    !getTranslation(editingCity.translations, 'en')?.name ||
                    !editingCity.country_id ||
                    editLoading
                  }
                >
                  {editLoading ? 'Сохранение...' : 'Сохранить изменения'}
                </Button>
              </div>
            </div>
          )}
        </Modal>
      </div>
    );
  },
);

export default CitySelector;
